/** Second-pass material and layout rules for the hero sheet, the spellbook and
 * the front-of-house screens.
 *
 * It is a separate stylesheet from `styles.ts` on purpose. `styles.ts` is shared
 * with the town screen; everything here is scoped to selectors only these
 * screens use, and it is appended after `SCREENS_CSS` so a rule of equal
 * specificity here wins. Where a shared rule has to be beaten (a `.hs-btn`
 * inside one of these screens, say) the selector carries an extra class rather
 * than `!important`, which keeps the cascade readable.
 *
 * The bar this is written to: no flat rectangle anywhere, no panel that is
 * mostly empty, no button that is a grey box with a label on it. Every plate is
 * a material - bevelled, grained, rimmed in gilt - and every surface that a
 * player looks at for longer than a second carries something worth reading.
 */


import { WOOD_FIBRE, WOOD_FIGURE, reliefDecls } from "../theme/relief.js";
import { FAINT, FDISP, G0, G1, G2, G3 } from "./cssTokens.js";
import { StyleLayer, useStyleSheet } from "../theme/styleSheet.js";

export const POLISH_STYLE_ID = "hm-screens-polish";


/* The panel ground, and it is HIDE rather than a board now.
 *
 * What stood here was four coprime stripe combs and four radial knots - a
 * plank, carefully de-periodised. The classic has no plank on any dialog: every
 * panel is dark tooled leather under a gilt rail (sampled (47,33,19), sd ~10,
 * no line anywhere on the HD hero sheet's ground), and a blind read named the
 * timber as the tell before it named anything else. The figure is the same
 * pair of aperiodic tiles `relief.ts` cuts every plate from, so a card, a bar
 * and a button are one skin. The two tiles share no factor, and the radial
 * pool under them is what keeps a 1920px bar from reading as one flat value. */
const BOARD_FIGURE = `
    ${WOOD_FIGURE},
    ${WOOD_FIBRE},
    radial-gradient(140% 100% at 22% 0%, rgba(126,88,48,0.40) 0%, rgba(0,0,0,0) 62%)`;

/* The same figure over the base the shared sheet's own wood was mixed at.
   `styles.ts` reads its wood through `var(--hm-wood-grain, <a 37px comb>)`, so
   declaring the token here is how the board reaches the dialogs, the town army
   bar and the spellbook tabs without a second copy of it living in that file.
   The base restates the comb's own three colours rather than this file's, so
   what changes is the figure and not the tone. */
const BOARD = `${BOARD_FIGURE},
    linear-gradient(178deg, #4a2f1a 0%, #3a2413 46%, #26170c 100%)`;
const WOOD_GRAIN = `${BOARD_FIGURE},
    linear-gradient(178deg, #54371f 0%, #3d2716 46%, #2c1c0f 100%)`;

/* THE BUILD'S ONE VELLUM, and the reason it is a token rather than a rule.
 *
 * Six screens had byte-identical copies of the same six-pixel comb at 97deg,
 * and it was the same failure the board above had: evenly spaced light lines
 * crossed by evenly spaced light lines is engineering paper. A copy cannot be
 * fixed once, so this is exposed as `--hm-vellum` and the call sites name it.
 *
 * The aperiodic content is two fractal-noise tiles whose sizes are coprime -
 * 157 and 211, so the pair repeats every 33,127px, which is seventeen screens
 * wide - and `stitchTiles` is what lets each one tile without a seam. The laid
 * lines on top are irregular WITHIN one period (0, 9, 23, 44, 58 of 67) rather
 * than evenly spaced, so the eye reads fibre and not a comb.
 *
 * A call site appends its own opaque ground AFTER the token, and must not set
 * `background-size` unless it restates a value for every layer.
 *
 * ## The noise is QUANTISED, and that is not a detail
 *
 * The first cut of this kept the turbulence's own continuous luminance, and a
 * continuous overlay over quantised art gives every source colour a new value
 * at every pixel: a 60x60 patch of the Hot Seat hillside went from 524 unique
 * colours to 968 - a photographic noise field over pixel art, which is the
 * same failure the smooth plate grade was rewritten for. So the filter throws
 * the turbulence's colour away, keeps a single fixed ink, and puts the
 * turbulence into the ALPHA through a `discrete` transfer with three entries:
 * the layer paints one colour at one of two strengths, or nothing at all. The
 * figure stays aperiodic - it is still fractal noise deciding which pixels -
 * and the palette it can add is two colours per layer rather than 256.
 *
 * `filterUnits='userSpaceOnUse'` with the tile's own box is the other half of
 * `stitchTiles`, and leaving it off is a seam. A filter region defaults to 10%
 * outside the object's bounding box, and `stitch` joins the noise across the
 * REGION, not across the tile - so the 211px sheet did not meet itself, and the
 * Journal's gutter carried a 7-luma step every 211px down the page. Pinning the
 * region to 0,0,px,px is what makes the join invisible. */
const vellumNoise = (
  px: number, freq: string, alpha: string,
  ink: readonly [string, string, string], bias: string, steps: string,
): string =>
  "url(\"data:image/svg+xml;utf8,"
  + `<svg xmlns='http://www.w3.org/2000/svg' width='${String(px)}' height='${String(px)}'>`
  + `<filter id='v' filterUnits='userSpaceOnUse' x='0' y='0'`
  + ` width='${String(px)}' height='${String(px)}'>`
  + `<feTurbulence type='fractalNoise' baseFrequency='${freq}'`
  + " numOctaves='4' stitchTiles='stitch'/>"
  + `<feColorMatrix type='matrix' values='0 0 0 0 ${ink[0]}`
  + ` 0 0 0 0 ${ink[1]} 0 0 0 0 ${ink[2]} 0.42 0.42 0.42 0 ${bias}'/>`
  + `<feComponentTransfer><feFuncA type='discrete' tableValues='${steps}'/>`
  + "</feComponentTransfer></filter>"
  + `<rect width='${String(px)}' height='${String(px)}' filter='url(%23v)'`
  + ` opacity='${alpha}'/></svg>")`;

const VELLUM = [
  vellumNoise(157, "0.78", "0.15", ["0.07", "0.048", "0.022"], "-0.20", "0 0 1"),
  vellumNoise(211, "0.14", "0.08", ["1", "0.91", "0.76"], "-0.28", "0 0 0.55 1"),
  "repeating-linear-gradient(96.4deg,"
  + " rgba(255,236,196,0.035) 0 1px, rgba(0,0,0,0) 1px 9px,"
  + " rgba(58,40,16,0.045) 9px 10px, rgba(0,0,0,0) 10px 23px,"
  + " rgba(255,236,196,0.025) 23px 24px, rgba(0,0,0,0) 24px 44px,"
  + " rgba(58,40,16,0.035) 44px 45px, rgba(0,0,0,0) 45px 58px,"
  + " rgba(255,236,196,0.030) 58px 59px, rgba(0,0,0,0) 59px 67px)",
].join(",\n    ");

/* THE KIT'S ONE PIECE OF DRAWN ORNAMENT, and the reason it is a token.
 *
 * Every frame in this build was a box-shadow lip - which is a border however
 * many stops it carries - so six screens of rectangles and hairlines stood
 * against a reference whose whole identity is gilt filigree: a carved block
 * closing each corner of the page. This is that block, authored once at 30px on
 * whole pixel coordinates with `shape-rendering: crispEdges`, so it rasterises
 * to hard steps at 1:1 like every other pixel on the screen rather than to the
 * smooth curve an SVG would default to.
 *
 * ## It is a BRACKET, not a band laid over the corner
 *
 * The first cut was one diagonal strap with both ends cut flat, and at 6x it
 * read as a piece of tape with a button on it: it crossed the corner without
 * touching either rail, so nothing it did was structural. This one has two
 * arms, 6px thick - the weight of the plate's own gilt lip - running the full
 * 30px along the top edge and the left edge, so the piece IS the two rails
 * where they meet and the diagonal tie is what joins them. Each arm carries the
 * same three values the rails do (a cream outer lip, a gold face, a shaded
 * inner edge), which is what makes the join invisible: the ornament and the
 * frame are drawn from one palette in one direction of light.
 *
 * ## The gem is drawn OUTSIDE the rotation
 *
 * Four tokens rather than one rotated element, because a background layer
 * cannot be rotated per corner - so the arms are rotated into place. A gem
 * rotated with them would be lit from the upper right on one corner and the
 * lower left on another, which is four light sources on one page. The elbow
 * moves under rotation and the light does not, so the stone is placed at the
 * rotated elbow and drawn unrotated: a black seat two pixels proud all round,
 * a cream upper facet, a shaded lower facet and a struck highlight. `%23` is
 * the `#` a colour literal needs inside a data URI. */
const gemAt = (cx: number, cy: number, r: number): string => {
  const s = (n: number): string => String(n);
  return `<path d='M${s(cx)} ${s(cy - r)} L${s(cx + r)} ${s(cy)} `
    + `L${s(cx)} ${s(cy + r)} L${s(cx - r)} ${s(cy)} Z' fill='%23140d04'/>`
    // The two facets meet on the horizontal, so the stone is lit from above and
    // seated below whatever corner it is set into.
    + `<path d='M${s(cx)} ${s(cy - r + 2)} L${s(cx + r - 2)} ${s(cy)} `
    + `L${s(cx - r + 2)} ${s(cy)} Z' fill='%23f6e3ac'/>`
    + `<path d='M${s(cx - r + 2)} ${s(cy)} L${s(cx + r - 2)} ${s(cy)} `
    + `L${s(cx)} ${s(cy + r - 2)} Z' fill='%236b4d15'/>`
    + `<path d='M${s(cx)} ${s(cy - r + 3)} L${s(cx + 1)} ${s(cy - r + 4)} `
    + `L${s(cx - 1)} ${s(cy - r + 4)} Z' fill='%23fff6dc'/>`;
};

const mitre = (deg: number): string => {
  // The elbow of the bracket after the rotation that puts its arms on the two
  // rails this corner belongs to.
  const ex = deg === 0 || deg === 270 ? 9 : 21;
  const ey = deg === 0 || deg === 90 ? 9 : 21;
  return "url(\"data:image/svg+xml;utf8,"
    + "<svg xmlns='http://www.w3.org/2000/svg' width='30' height='30'"
    + " shape-rendering='crispEdges'>"
    + `<g transform='rotate(${String(deg)} 15 15)'>`
    // The bracket: two arms and the diagonal that ties them.
    + "<path d='M0 0 H30 V6 H6 V30 H0 Z' fill='%23140d04'/>"
    + "<path d='M0 23 L23 0 L15 0 L0 15 Z' fill='%23140d04'/>"
    + "<path d='M1 1 H29 V2 H2 V29 H1 Z' fill='%23f2dba4'/>"
    + "<path d='M2 2 H29 V5 H5 V29 H2 Z' fill='%23d9b25a'/>"
    + "<path d='M2 5 H29 V6 H6 V29 H5 V6 H2 Z' fill='%238a6620'/>"
    + "<path d='M2 20 L20 2 L17 2 L2 17 Z' fill='%23d9b25a'/>"
    + "<path d='M2 17 L17 2 L15 2 L2 15 Z' fill='%238a6620'/>"
    + "</g>"
    + gemAt(ex, ey, 6)
    + "</svg>\")";
};

/* The terminus the same ornament reduces to where a corner has no room for a
   bracket: the gem alone, on the rail it interrupts. Same drawing, same light,
   so a cartouche and a page are ornamented out of one figure. */
const GEM = "url(\"data:image/svg+xml;utf8,"
  + "<svg xmlns='http://www.w3.org/2000/svg' width='13' height='13'"
  + " shape-rendering='crispEdges'>"
  + gemAt(6, 6, 6)
  + "</svg>\")";

/* CARVED, SOCKET_BG and SOCKET_RIM used to live here: this file's own copies of
   a chamfer and a recess, drawn a third time after `theme/css.ts` and
   `screens/styles.ts` had each drawn their own. They are gone - every plate and
   every socket below goes through `reliefDecls`, so there is one place that
   decides which way the light falls. */

/* The figure behind the doll. Etched, not drawn: it has to say head, shoulders,
   hands and feet without competing with nineteen pieces of artifact art. */
const FIGURE = "url(\"data:image/svg+xml;utf8,"
  + "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 134'>"
  + "<g fill='rgba(255,236,200,0.055)' stroke='rgba(238,214,168,0.30)' stroke-width='2.4'"
  + " stroke-linejoin='round' stroke-linecap='round'>"
  + "<circle cx='50' cy='15' r='10.5'/>"
  + "<path d='M40 29 L33 33 L34 48 L32 86 L68 86 L66 48 L67 33 L60 29 Z'/>"
  + "<path d='M33 33 L23 38 L17 62 L14 84 L23 86 L28 63 L34 48'/>"
  + "<path d='M67 33 L77 38 L83 62 L86 84 L77 86 L72 63 L66 48'/>"
  + "<path d='M37 86 L34 124 L45 124 L48 92'/>"
  + "<path d='M63 86 L66 124 L55 124 L52 92'/>"
  + "</g></svg>\")";

export const POLISH_CSS = `
/* ============================================================ kit tokens */

/* Declared on the root rather than on a class, because the point of a token is
   that a screen can reach it without importing anything. */
.hm-root {
  --hm-vellum:
    ${VELLUM};
  --hm-wood-grain:
    ${WOOD_GRAIN};
  --hm-corner-tl: ${mitre(0)};
  --hm-corner-tr: ${mitre(90)};
  --hm-corner-br: ${mitre(180)};
  --hm-corner-bl: ${mitre(270)};
  --hm-gem: ${GEM};
}

/* ================================================= shared: carved plates */

/* A plate: bronze under gilt, bevelled top and bottom, with the label cut into
   it rather than printed on it. Used for every button on these screens. */
.hs-plate {
  position: relative;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 8px 20px;
  border: 0;
  cursor: pointer;
  font-family: ${FDISP};
  font-size: max(13px, var(--hm-type-floor));
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: #f2dda6;
${reliefDecls("chamfer", "wood", 3)}
  text-shadow: 0 -1px 0 rgba(0,0,0,0.95), 0 1px 0 rgba(255,222,160,0.22);
  transition: filter 90ms linear;
}
/* The studs. Two of them, one at each end, so the plate reads as riveted metal
   and not as a rounded-off web button. */
.hs-plate::before, .hs-plate::after {
  content: "";
  width: 6px; height: 6px;
  flex: 0 0 auto;
  transform: rotate(45deg);
  background-image: linear-gradient(135deg, #f6e3ac, ${G2} 55%, #4b3410);
  box-shadow: 0 0 0 1px rgba(0,0,0,0.7), 0 1px 2px rgba(0,0,0,0.7);
}
/* THE RAIL CLOSES THE FRAME, and it is a border because it has to sit OUTSIDE
   the chamfer.
   \`reliefDecls\` gives a plate four lips, and by design the bottom one is the
   seat - the darkest value on the control, the shadow the plate throws on its
   own panel. That is right for a bevel and wrong for a frame: what it renders
   is gold along the top and the two sides and plain black along the foot, so
   the vertical rails end in stubs and the CLOSE plate on Journal and Hot Seat
   reads as three-sided. The classic closes every socket and every button with a
   continuous one-pixel rail and puts the bevel inside it, which is exactly what
   a border does here: inset shadows are drawn against the padding box, so the
   border ring is the one edge the chamfer cannot paint over. \`border-box\` is
   the kit default (\`theme/css.ts\`), so nothing moves.
   Restated on \`:active\` because \`reliefDecls\` emits its own \`border: 0\`. */
.hs-plate { border: 1px solid rgba(150,113,36,0.85); }
.hs-plate:hover:not(:disabled) { filter: brightness(1.18) saturate(1.05); border-color: ${G2}; }
.hs-plate:active:not(:disabled) {
  transform: translateY(2px);
${reliefDecls("recess", "wood", 3)}
  border: 1px solid rgba(104,78,26,0.9);
}
/* Every button on these screens gets the same closing rail, or one screen has
   two conventions for the same object. */
.hs-screen .hs-btn { border: 1px solid rgba(140,105,34,0.75); }
.hs-screen .hs-btn--gold { border-color: rgba(168,128,31,0.9); }
.hs-screen .hs-btn:active:not(:disabled) { border: 1px solid rgba(96,72,24,0.9); }
/* Chrome's own ring is a 2px pure #ffffff, a value that appears nowhere in this
   palette, and Hot Seat's confirm button carries \`autoFocus\` so it was on the
   very first paint of the screen. Gilt, and outside the rail rather than over
   it, so the frame underneath still reads. */
.hs-plate:focus-visible,
.hs-screen .hs-btn:focus-visible,
.hs-spell:focus-visible,
.hs-shelf:focus-visible {
  outline: 2px solid #f0d089;
  outline-offset: 2px;
}
.hs-plate:disabled { cursor: not-allowed; color: #8b7853; filter: saturate(0.3) brightness(0.7); }
.hs-plate:disabled::before, .hs-plate:disabled::after { opacity: 0.35; }
.hs-plate--wide { width: 100%; }
/* The rail is restated here for the same reason it is on \`:active\`: every
   \`reliefDecls\` block carries its own \`border: 0\`, and this rule is later in
   the sheet than the base one. */
.hs-plate--on {
  color: #2a1c06;
${reliefDecls("chamfer", "bronze", 3)}
  border: 1px solid rgba(168,128,31,0.9);
  text-shadow: 0 1px 0 rgba(255,255,255,0.42);
}
.hs-plate--sm { padding: 5px 12px; font-size: max(11px, var(--hm-type-floor)); letter-spacing: 0.10em; gap: 7px; }
.hs-plate--sm::before, .hs-plate--sm::after { width: 4px; height: 4px; }
/* THE FOOT GETS THE THREE PIXELS THE HEAD HAS.
   The rail above closes the frame, but \`reliefDecls\` makes the bottom lip the
   seat - the darkest value on the control, the shadow it throws on its own
   panel - so what a 1:1 reading of the CLOSE plate found was four pixels of
   cream at the head and one near-black pixel at the foot sitting behind three
   of black. It closes for a pixel probe and not for the eye. Three pixels of
   the rail's own gold at the foot is the same weight as the head's lip at the
   value a lit-from-above frame takes, which is what stops it reading as
   gold-top-and-sides. Listed after every rule that restates \`border\`, because
   each \`reliefDecls\` block emits its own \`border: 0\`. */
.hs-plate { border-bottom-width: 3px; border-bottom-color: rgba(132,99,32,0.95); }
.hs-plate--on { border-bottom-color: rgba(150,113,28,0.95); }
.hs-plate:active:not(:disabled) {
  border-bottom-width: 3px;
  border-bottom-color: rgba(90,66,20,0.92);
}

/* Buttons that stayed on the shared '.hs-btn' inside these screens still get a
   proper bevel rather than a slab. */
.hs-screen .hs-btn { padding: 6px 15px; }

/* =============================================================== main menu */

/* The painting is pixel art magnified at a whole factor by ScaledSprite and
   cropped by the frame, so every source pixel is the same size on screen.
   'pixelated' is the other half of that: 'auto' reintroduces the bilinear ramp
   the whole scale exists to avoid. The crop is anchored to the top edge so the
   sky, the cloud band and the ridge line all survive it. */
.hs-menu__art--cover {
  left: 50%;
  top: 0;
  transform: translateX(-50%);
  image-rendering: pixelated;
  opacity: 0.95;
  filter: saturate(1.02) contrast(1.03) brightness(0.97);
}
/* The veil seats the title in shade. Its old final stop was opaque black at
   100%, which crushed the bottom 240px of the frame - 22% of the height - to
   nothing, so the grass never reached the bottom edge and the buttons floated
   over a void. It now bottoms out at a deep shade the grass still reads
   through, and the foot rail carries the very bottom. */
.hs-menu__veil {
  background:
    radial-gradient(86% 76% at 50% 30%, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.30) 66%, rgba(0,0,0,0.66) 100%),
    linear-gradient(180deg, rgba(6,8,14,0.55) 0%, rgba(6,8,14,0.04) 26%,
      rgba(6,5,3,0.18) 56%, rgba(6,5,3,0.44) 82%, rgba(6,5,3,0.30) 100%);
}
/* God rays. The hard-edged conic wedges read as a CSS overlay: they crossed sky,
   cloud and mountain at one opacity and stopped dead at the horizon. Blurred at
   the source, ramped rather than stepped, and masked so they attenuate with
   distance from the sun and fade out before the ridge line. */
.hs-menu__rays {
  inset: -24% -10% 0;
  background-image: repeating-conic-gradient(from 0deg at 50% 12%,
    rgba(255, 240, 202, 0) 0deg 0.6deg,
    rgba(255, 240, 202, 0.085) 0.6deg 2.2deg,
    rgba(255, 240, 202, 0.030) 2.2deg 4.4deg,
    rgba(255, 240, 202, 0) 4.4deg 11deg);
  filter: blur(7px);
  -webkit-mask-image: radial-gradient(44% 40% at 50% 12%,
    rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.55) 42%, rgba(0,0,0,0.16) 72%, transparent 92%);
  mask-image: radial-gradient(44% 40% at 50% 12%,
    rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.55) 42%, rgba(0,0,0,0.16) 72%, transparent 92%);
}
.hs-menu { gap: 0; justify-content: flex-start; }
/* The stack sits low enough that its centre of mass is below the frame centre;
   the old column ended at 73% of the height with nothing under it. */
.hs-menu__stack {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  margin-top: 19vh;
}
/* Gilt, not cream: the letterform is filled with the same metal the plates are
   rimmed with, and the drop is two hard steps plus a cast shadow so it reads as
   raised bronze rather than as text with a blur behind it. */
/* \`--hm-title-size\` rather than a literal clamp, because a call site that wants
   a different size has to restate the whole rule otherwise - and the whole rule
   includes a five-stage \`filter\` chain, which does not merge: declaring
   \`font-size\` at the call site is free, but the moment a second rule wants to
   add a contour to the drop it must repeat all five drop-shadows in the right
   order or lose the ones it did not repeat. One property is the difference
   between overriding the size and forking the treatment. */
.hs-menu__title {
  font-size: var(--hm-title-size, clamp(46px, 5.4vw, 88px));
  color: #ffe9b6;
  background-image: linear-gradient(178deg, #fffdf2 0%, #ffeeb8 38%, #e8bf62 66%, #fff4d2 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  filter:
    drop-shadow(0 2px 0 #5d3f11)
    drop-shadow(0 4px 0 #231806)
    drop-shadow(0 10px 26px rgba(0,0,0,0.95));
}
.hs-menu__rule { width: min(720px, 62vw); max-width: none; }
/* The mountains behind the title are the busiest band in the painting, so the
   crest carries its own pool of shade; without it the gilt letterforms and the
   ridge line interleave and the title stops reading at a glance. */
.hs-menu__crest::before {
  content: "";
  position: absolute;
  inset: -18% -24%;
  z-index: -1;
  pointer-events: none;
  background: radial-gradient(58% 62% at 50% 52%,
    rgba(4,6,12,0.72) 0%, rgba(4,6,12,0.44) 52%, rgba(4,6,12,0) 100%);
}
.hs-menu__sub { font-size: max(clamp(13px, 1.1vw, 18px), var(--hm-type-floor)); }
/* The base class is metrics only. It used to carry a board, a three-layer gilt
   bead and a drop shadow as well, and every one of those is a CONTAINER - a
   decision about whether the menu sits on an object or on the scene. That is
   not a decision a base class gets to make: the title screen correctly has no
   plate behind its words, and had to undo four properties one at a time to say
   so, of which \`background-image: none\` and \`box-shadow: none\` are the two that
   are easy to forget and invisible when you do.

   A menu that does want a plinth asks for one. \`.hs-menu__items--plinth\` is
   what was here. */
.hs-menu__items {
  min-width: min(440px, 42vw);
  gap: 12px;
  margin-top: 10px;
}
/* The plates stand on a plinth rather than on grass, so the column reads as an
   object placed in the scene. */
.hs-menu__items--plinth {
  padding: 18px 20px;
  background-color: #2b1c0f;
  background-image: ${BOARD};
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.9),
    inset 0 0 0 2px ${G1},
    inset 0 0 0 3px rgba(0,0,0,0.8),
    inset 0 3px 18px rgba(0,0,0,0.55),
    inset 0 -20px 34px rgba(0,0,0,0.3),
    0 10px 34px rgba(0,0,0,0.75);
}
.hs-menu__item { padding: 13px 26px; font-size: max(clamp(14px, 1.05vw, 18px), var(--hm-type-floor)); letter-spacing: 0.2em; }

/* ---- the foot rail */
.hs-menu__rail {
  position: absolute;
  left: 0; right: 0; bottom: 0;
  z-index: 2;
  height: 58px;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 26px;
  background-color: #24170c;
  background-image:
    linear-gradient(180deg, rgba(255,224,170,0.12) 0 2px, rgba(0,0,0,0) 2px 100%),
    ${BOARD};
  box-shadow:
    inset 0 1px 0 rgba(255,232,186,0.22),
    inset 0 2px 0 rgba(0,0,0,0.6),
    0 -10px 26px rgba(0,0,0,0.55);
}
.hs-menu__railrule {
  flex: 1 1 auto;
  height: 3px;
  background-image:
    linear-gradient(90deg, rgba(168,128,31,0) 0%, ${G2} 14%, ${G3} 50%, ${G2} 86%, rgba(168,128,31,0) 100%);
  box-shadow: 0 1px 0 rgba(0,0,0,0.7);
}
.hs-menu__stud {
  flex: 0 0 auto;
  width: 13px; height: 13px;
  transform: rotate(45deg);
  background-image: linear-gradient(135deg, #f6e3ac, ${G2} 55%, #4b3410);
  box-shadow: 0 0 0 1px rgba(0,0,0,0.75), 0 2px 3px rgba(0,0,0,0.7);
}
.hs-menu__stamp {
  position: absolute;
  right: 16px;
  bottom: 6px;
  font-family: ${FDISP};
  font-size: max(10px, var(--hm-type-floor));
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${FAINT};
}
/* A gilt corner rule at the top. The bottom is the foot rail's job now: the old
   34%-tall black gradient was the reason the lower third of the frame was
   empty. */
.hs-menu::before {
  content: "";
  position: absolute;
  left: 0; right: 0;
  top: 0;
  height: 46px;
  pointer-events: none;
  z-index: 1;
  background: linear-gradient(180deg, rgba(0,0,0,0.72), rgba(0,0,0,0));
}
.hs-menu::after {
  content: "";
  position: absolute;
  left: 0; right: 0;
  bottom: 0;
  height: 22%;
  pointer-events: none;
  z-index: 1;
  background: linear-gradient(0deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.12) 46%, rgba(0,0,0,0) 100%);
}

/* The header and footer strips. The shared rule paints them with a single
   repeating stripe, which at full width reads as corduroy; this is the same
   board the panels use, lit from the top edge. */
.hs-plank {
  background-color: #24170c;
  background-image:
    linear-gradient(180deg, rgba(255,224,170,0.10) 0 2px, rgba(0,0,0,0) 2px 100%),
    ${BOARD};
}

/* ---- 1366x768. Same panels, same contents, smaller sprites and tighter
   mouldings. Nothing is dropped: a sheet that hides the war-machine sockets on
   a laptop is a sheet that lies about what the hero is carrying. */
.hs-hero--compact .hs-hero { grid-template-columns: 292px minmax(0, 1fr); gap: 9px; padding: 9px; }
.hs-hero--compact .hs-hero__main { grid-template-columns: minmax(0, 1fr) 288px 288px; gap: 9px; }
.hs-hero--compact .hs-hero__col, .hs-hero--compact .hs-hero__gear { gap: 9px; }
.hs-hero--compact .hs-card { padding: 7px 9px 8px; }
.hs-hero--compact .hs-card__title { margin-bottom: 5px; padding-bottom: 4px; }
/* The frame and its inner keyline move together, which is why this is a custom
   property and not a \`padding\` - overriding the padding alone left the ::after
   keyline sitting where a 12px frame had put it. */
.hs-hero--compact .hs-portrait { --hs-portrait-frame: 8px; }
.hs-hero--compact .hs-hero__name { font-size: max(17px, var(--hm-type-floor)); margin-top: 5px; }
.hs-hero--compact .hs-hero__hair { margin: 6px 0 5px; }
.hs-hero--compact .hs-special__name { font-size: max(14px, var(--hm-type-floor)); }
.hs-hero--compact .hs-primaries { gap: 5px; }
.hs-hero--compact .hs-prim { padding: 5px 2px 4px; }
.hs-hero--compact .hs-prim__value { font-size: max(19px, var(--hm-type-floor)); }
.hs-hero--compact .hs-prim__label { font-size: max(8px, var(--hm-type-floor)); letter-spacing: 0.06em; }
.hs-hero--compact .hs-skills { gap: 6px; }
.hs-hero--compact .hs-skill { padding: 6px 5px 5px; gap: 3px; }
.hs-hero--compact .hs-skill__name { font-size: max(12px, var(--hm-type-floor)); }
.hs-hero--compact .hs-skill__level { font-size: max(9px, var(--hm-type-floor)); letter-spacing: 0.14em; }
.hs-hero--compact .hs-doll { padding: 7px 6px; gap: 4px; }
.hs-hero--compact .hs-doll .hs-slot { max-width: 48px; }
.hs-hero--compact .hs-backpack { gap: 4px; }
.hs-hero--compact .hs-backpack .hs-slot { max-width: 48px; }
.hs-hero--compact .hs-slot__ghost { font-size: max(7px, var(--hm-type-floor)); letter-spacing: 0.06em; }
.hs-hero--compact .hs-hero .hs-armyrow .hs-slot { width: 84px; height: 84px; }
.hs-hero--compact .hs-hero .hs-slot__count { min-width: 26px; font-size: max(11px, var(--hm-type-floor)); line-height: 14px; }
.hs-hero--compact .hs-shelf { padding: 5px 6px; gap: 3px; }
.hs-hero--compact .hs-shelf__rank { width: 44px; font-size: max(9px, var(--hm-type-floor)); letter-spacing: 0.12em; }
.hs-hero--compact .hs-shelf__icons { gap: 3px; }
.hs-hero--compact .hs-xp__now { font-size: max(17px, var(--hm-type-floor)); }
.hs-hero--compact .hs-bar { height: 14px; }
.hs-hero--compact .hs-hero .hm-statline { padding: 2px 0; }

/* =============================================================== spellbook */

/* The desk the book is open on. Without it the spread floats on the shell's
   flat stone wash, which is the one surface on these screens that was still a
   CSS box rather than a material. */
.hs-book {
  /* Stretch, not centre: the spread is the screen, and a book that sits as a
     postcard in the middle of 1080p is the defect this replaces. */
  align-items: stretch;
  padding: 10px 16px 12px;
  gap: 0;
  background-color: #24170c;
  background-image:
    radial-gradient(120% 90% at 50% 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0.75) 100%),
    ${BOARD};
}
/* Covers. The spread is a bound book: tooled leather, a gilt edge, and the
   pages inset within it. */
/* \`position: relative\` and a real outer margin, because the school ribbons hang
   off the OUTER EDGE of the book in the classic and could not here. They sit at
   the foot of the verso page instead, and the reason is geometric rather than a
   choice: the spread was \`position: static\`, so an absolutely-positioned strip
   resolved against the screen; and its padding was 10px, so even a relative one
   had 10px of leather to hang into at 1366. Two properties, and the strip can
   be hung where the reference hangs it. */
.hs-book__spread {
  position: relative;
  flex: 1 1 auto;
  display: flex;
  width: 100%;
  max-width: 1640px;
  max-height: none;
  align-self: stretch;
  margin: 0 auto;
  padding: 10px 34px;
  background-color: #2a1408;
  background-image:
    repeating-linear-gradient(126deg, rgba(255,220,170,0.045) 0 2px, rgba(0,0,0,0.10) 2px 5px),
    radial-gradient(120% 130% at 50% 0%, #5a2f14 0%, #34190a 55%, #1c0d04 100%);
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.9),
    inset 0 0 0 3px ${G1},
    inset 0 0 0 4px rgba(0,0,0,0.85),
    inset 0 0 46px rgba(0,0,0,0.6),
    0 18px 56px rgba(0,0,0,0.9);
}
.hs-book__page {
  flex: 1 1 0;
  width: auto;
  min-width: 0;
  padding: 14px 22px 11px;
  gap: 8px;
  /* Real paper: the kit's vellum plus foxing at the outer corner, and the shadow
     of the gutter falling across the inner edge. The laid tint that was here was
     the same four-pixel comb the six cartouches carried; it is the token now, so
     there is one paper in the build and it can be fixed in one place. */
  background-image:
    var(--hm-vellum),
    radial-gradient(80% 60% at 88% 96%, rgba(140,102,48,0.30) 0%, rgba(0,0,0,0) 60%),
    radial-gradient(120% 140% at 50% 12%, #f2e3bd 0%, #dcc697 46%, #b79f72 100%);
}
.hs-book__page--left { box-shadow:
  inset 0 0 0 1px rgba(70,52,26,0.7),
  inset -26px 0 34px rgba(70,48,18,0.42),
  inset 0 0 60px rgba(120,88,42,0.24); }
.hs-book__page--right { box-shadow:
  inset 0 0 0 1px rgba(70,52,26,0.7),
  inset 26px 0 34px rgba(70,48,18,0.42),
  inset 0 0 60px rgba(120,88,42,0.24); }
/* The spine. A bound book is stitched in signatures, not perforated: the old
   nine-pixel ladder ran a hundred and twelve identical rungs from the head of
   the page to the foot with nothing at either end, which down the middle of a
   1080p spread is a barcode.
   So: a head band and a tail band that cap the run (the two no-repeat
   gradients, positioned at 0 and 100%), and between them a stitch whose period
   is 47px and carries FOUR unequal rungs rather than one - the eye finds the
   group, not the pitch. 47 against the 26px width shares no factor, so the
   pattern does not line up with the spine's own edges either. */
.hs-book__gutter {
  width: 26px;
  background-image:
    linear-gradient(180deg,
      rgba(255,230,182,0.24) 0 2px, rgba(0,0,0,0.55) 2px 5px,
      rgba(255,222,170,0.14) 5px 7px, rgba(0,0,0,0.42) 7px 11px,
      rgba(0,0,0,0) 11px 19px),
    linear-gradient(0deg,
      rgba(255,230,182,0.20) 0 2px, rgba(0,0,0,0.55) 2px 5px,
      rgba(255,222,170,0.12) 5px 7px, rgba(0,0,0,0.40) 7px 11px,
      rgba(0,0,0,0) 11px 19px),
    repeating-linear-gradient(180deg,
      rgba(255,222,170,0.11) 0 2px, rgba(0,0,0,0.24) 2px 7px,
      rgba(255,222,170,0.06) 7px 8px, rgba(0,0,0,0.18) 8px 17px,
      rgba(255,222,170,0.10) 17px 19px, rgba(0,0,0,0.26) 19px 27px,
      rgba(255,222,170,0.05) 27px 28px, rgba(0,0,0,0.16) 28px 47px),
    linear-gradient(90deg, #7a5c2c 0%, #2a1c0a 42%, #150d04 50%, #2a1c0a 58%, #7a5c2c 100%);
  background-size: 100% 19px, 100% 19px, auto, auto;
  background-position: 0 0, 0 100%, 0 0, 0 0;
  background-repeat: no-repeat, no-repeat, repeat, no-repeat;
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.7);
}
.hs-book__head {
  font-size: max(clamp(15px, 1.3vw, 24px), var(--hm-type-floor));
  color: #4a3410;
  letter-spacing: 0.16em;
  padding-bottom: 8px;
  flex: 0 0 auto;
  text-shadow: 0 1px 0 rgba(255,246,215,0.55);
  /* An illuminated rule under the heading, mitred at both ends. */
  background-image: linear-gradient(90deg,
    rgba(90,66,26,0) 0%, rgba(90,66,26,0.75) 12%, rgba(90,66,26,0.75) 88%, rgba(90,66,26,0) 100%);
  background-size: 100% 1px;
  background-position: 0 100%;
  background-repeat: no-repeat;
}
.hs-book__list {
  display: flex;
  flex-direction: column;
  gap: 9px;
  justify-content: flex-start;
  flex: 1 1 auto;
  min-height: 0;
  /* \`clip\` with a margin, not \`hidden\`: a bitmap caption's box is the ink,
     and under a fractional device ratio the box lands on a fractional device
     pixel while crispEdges snaps each texel rectangle to a whole one, so an
     edge row can be snapped just past the box and a plain clip shaves it.
     Measured at 1366x768 100%: the fourth row's \`Spell Points: 15\` hung
     1.19 device px below this box and lost its baseline row. The margin costs
     no layout and clips everything a reader would call a spill. */
  overflow: clip;
  overflow-clip-margin: 2px;
}

/* ---- level bands. A book paged by level is how a player finds "my level
   three spells"; nine-pixel grey type under the name is not. */
.hs-band { display: flex; flex-direction: column; gap: 5px; }
.hs-band__rule {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${FDISP};
  font-size: max(11px, var(--hm-type-floor));
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #6d5017;
  text-shadow: 0 1px 0 rgba(255,248,222,0.6);
  white-space: nowrap;
}
.hs-band__rule::after {
  content: "";
  flex: 1 1 auto;
  height: 3px;
  background-image:
    linear-gradient(90deg, rgba(109,80,23,0.65) 0%, rgba(109,80,23,0.10) 92%, rgba(109,80,23,0) 100%);
  background-size: 100% 1px;
  background-position: 0 1px;
  background-repeat: no-repeat;
  box-shadow: 0 1px 0 rgba(255,248,222,0.5);
}
.hs-band__cells {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 10px;
}

/* One entry in the index. Two to a row, so the tile is the width of its own
   contents: a 700px bar holding a 64px icon and two short lines leaves five
   hundred pixels of blank parchment, twenty times down the spread. */
.hs-spell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 0;
  padding: 4px 8px 4px 4px;
  border: 0;
  cursor: pointer;
  text-align: left;
  background:
    linear-gradient(180deg, rgba(255,248,222,0.52), rgba(255,248,222,0.10) 46%, rgba(120,92,44,0.14));
  box-shadow:
    inset 0 0 0 1px rgba(96,72,32,0.30),
    inset 0 1px 0 rgba(255,250,230,0.6),
    0 1px 0 rgba(120,96,52,0.20);
}
.hs-spell:hover:not(:disabled) {
  background: linear-gradient(180deg, rgba(255,252,236,0.78), rgba(226,196,132,0.30));
  box-shadow: inset 0 0 0 1px #6b4d15, inset 0 0 12px rgba(160,124,52,0.30);
}
.hs-spell--on {
  background: linear-gradient(180deg, rgba(255,246,206,0.95), rgba(214,176,96,0.45));
  box-shadow: inset 0 0 0 2px #6b4d15, inset 0 0 16px rgba(150,112,40,0.30);
}
/* Not castable this moment. The tile stays live and readable - the reading page
   carries the kernel's reason - and only the ink dims. */
.hs-spell--barred .hs-spell__text { opacity: 0.62; }
.hs-spell__socket {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px;
  isolation: isolate;
${reliefDecls("recess", "bronze", 2)}
}
.hs-spell__text { display: flex; flex-direction: column; gap: 1px; min-width: 0; flex: 1 1 auto; }
.hs-spell__name { font-family: ${FDISP}; font-size: max(clamp(13px, 0.95vw, 17px), var(--hm-type-floor));
  letter-spacing: 0.02em; color: #1d1406; text-shadow: 0 1px 0 rgba(255,248,222,0.55); }
.hs-spell__meta { font-size: max(clamp(10px, 0.7vw, 13px), var(--hm-type-floor)); color: #5b4520; }
.hs-spell__school {
  display: inline-block;
  width: 8px; height: 8px;
  margin-right: 5px;
  transform: rotate(45deg) translateY(-1px);
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.55);
}
.hs-spell__school--air { background-image: linear-gradient(135deg, #dff0ff, #6f9fd0); }
.hs-spell__school--earth { background-image: linear-gradient(135deg, #d9e6b0, #5f7a33); }
.hs-spell__school--fire { background-image: linear-gradient(135deg, #ffd9a2, #b4441c); }
.hs-spell__school--water { background-image: linear-gradient(135deg, #cfe9f5, #2f6b96); }
/* The cost is a disc struck into the page - bronze in the parchment's own value
   range, serif numerals, a lit top edge and its own shadow on the paper. The
   blue rounded rectangle it replaces was the only cool, hard-edged thing on the
   spread and it out-contrasted the spell art beside it twenty times over. */
.hs-spell__cost {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  font-family: ${FDISP};
  font-size: max(15px, var(--hm-type-floor));
  letter-spacing: 0;
  color: #fbedc6;
  background-color: #5a4118;
  /* The lit top edge is a third as bright as it was and the body a step darker:
     the numeral measured 3.5-3.9:1 against the disc's own highlight (ground
     (139,114,64) under the reader's \`5\` at 1920x1080), under the 4.5:1 a
     caption needs; the numeral is the reading, the sheen is not. */
  background-image:
    radial-gradient(62% 58% at 34% 24%, rgba(255,244,214,0.2) 0%, rgba(255,244,214,0) 62%),
    radial-gradient(120% 110% at 52% 116%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 58%),
    linear-gradient(168deg, #7d5f27 0%, #56401a 50%, #2f2109 100%);
  box-shadow:
    inset 0 0 0 1px rgba(38,26,6,0.85),
    inset 0 1px 0 rgba(255,242,206,0.55),
    inset 0 -2px 3px rgba(0,0,0,0.55),
    0 1px 0 rgba(255,252,235,0.65),
    0 2px 5px rgba(88,64,26,0.55);
  text-shadow: 0 1px 0 rgba(0,0,0,0.6);
}
/* Beyond this hero's mana: the same die struck in red wax. */
.hs-spell__cost--short {
  color: #ffe0d2;
  background-color: #6d2415;
  background-image:
    radial-gradient(62% 58% at 34% 24%, rgba(255,220,200,0.45) 0%, rgba(255,220,200,0) 62%),
    radial-gradient(120% 110% at 52% 116%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 58%),
    linear-gradient(168deg, #a5432a 0%, #6d2415 52%, #360f08 100%);
}
/* max(): 22px reaches the glass at 17.6 device px under an 80% page, under the 1080p type floor of 19. */
.hs-spell__cost--big { width: 52px; height: 52px; font-size: max(22px, var(--hm-type-floor)); }

/* ---- the reading page */
.hs-read { display: flex; flex-direction: column; gap: 10px; flex: 1 1 auto; min-height: 0;
  justify-content: space-between; }
/* The illumination. A pool of shade behind the art so a 256px sprite sits on
   the page rather than floating over it. */
.hs-read__plate {
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  isolation: isolate;
${reliefDecls("recess", "bronze", 3)}
}
.hs-read__banner {
  display: flex;
  align-items: center;
  gap: 9px;
  padding-bottom: 8px;
  background-image: linear-gradient(90deg,
    rgba(90,66,26,0.55) 0%, rgba(90,66,26,0.18) 70%, rgba(90,66,26,0) 100%);
  background-size: 100% 1px;
  background-position: 0 100%;
  background-repeat: no-repeat;
}
.hs-read__kind {
  font-family: ${FDISP};
  font-size: max(clamp(12px, 0.95vw, 17px), var(--hm-type-floor));
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: #5b4520;
  text-shadow: 0 1px 0 rgba(255,248,222,0.55);
}
.hs-read__body {
  margin: 0;
  font-family: ${FDISP};
  font-size: max(clamp(14px, 1.05vw, 19px), var(--hm-type-floor));
  line-height: 1.6;
  color: #2c2008;
  text-shadow: 0 1px 0 rgba(255,248,222,0.5);
}
/* The mastery ladder: what the same spell costs at each school mastery, the
   current rung struck brighter. */
.hs-read__ladder { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; }
.hs-rung {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 7px 4px 6px;
  background:
    linear-gradient(180deg, rgba(255,248,222,0.42), rgba(255,248,222,0.06) 50%, rgba(120,92,44,0.14));
  box-shadow:
    inset 0 0 0 1px rgba(96,72,32,0.28),
    inset 0 1px 0 rgba(255,250,230,0.55),
    0 1px 0 rgba(120,96,52,0.20);
}
.hs-rung--on {
  background: linear-gradient(180deg, rgba(255,246,206,0.95), rgba(214,176,96,0.42));
  box-shadow: inset 0 0 0 2px #6b4d15, inset 0 0 14px rgba(150,112,40,0.28);
}
.hs-rung__cost { font-family: ${FDISP}; font-size: max(19px, var(--hm-type-floor)); color: #2c2008;
  text-shadow: 0 1px 0 rgba(255,248,222,0.6); }
.hs-rung__name { font-family: ${FDISP}; font-size: max(10px, var(--hm-type-floor)); letter-spacing: 0.15em;
  text-transform: uppercase; color: #6a5227; }

.hs-read__facts { display: flex; flex-direction: column; gap: 2px; }
.hs-read__facts .hm-statline__label { color: #6a5227; font-size: max(12px, var(--hm-type-floor)); letter-spacing: 0.07em; }
.hs-read__facts .hm-statline__value { font-family: ${FDISP}; font-size: max(15px, var(--hm-type-floor)); color: #2c2008;
  text-shadow: 0 1px 0 rgba(255,248,222,0.5); }
.hs-read__facts .hm-statline__leader {
  background-image: linear-gradient(90deg, rgba(109,80,23,0.45) 0 100%);
  background-size: 100% 1px;
  background-position: 0 70%;
  background-repeat: repeat-x;
  opacity: 0.55;
}
.hs-read__act { display: flex; align-items: center; gap: 12px; padding-top: 10px; }
.hs-read__ready { font-family: ${FDISP}; font-size: max(13px, var(--hm-type-floor)); letter-spacing: 0.12em;
  text-transform: uppercase; color: #4c6a2a; text-shadow: 0 1px 0 rgba(255,248,222,0.55); }
.hs-book__foot {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 8px;
  margin-top: auto;
  flex: 0 0 auto;
  background-image: linear-gradient(90deg,
    rgba(90,66,26,0) 0%, rgba(90,66,26,0.6) 12%, rgba(90,66,26,0.6) 88%, rgba(90,66,26,0) 100%);
  background-size: 100% 1px;
  background-position: 0 0;
  background-repeat: no-repeat;
}
.hs-book__folio {
  margin-left: auto;
  font-family: ${FDISP};
  font-size: max(12px, var(--hm-type-floor));
  letter-spacing: 0.14em;
  text-transform: uppercase;
  /* #6a512a measured 3.6:1 on the leaf; #4a3414 is 5.6:1. */
  color: #4a3414;
  white-space: nowrap;
}
.hs-book__tabs { gap: 5px; justify-content: flex-start; flex-wrap: wrap; }
/* The book's own plates are darker than the menu's: they sit on parchment. */
.hs-book .hs-plate { box-shadow:
  inset 0 0 0 1px rgba(0,0,0,0.9), inset 0 0 0 2px ${G1}, inset 0 0 0 3px ${G0},
  inset 0 2px 0 rgba(255,236,196,0.18), inset 0 -2px 5px rgba(0,0,0,0.6),
  0 2px 0 rgba(0,0,0,0.35), 0 3px 8px rgba(90,66,30,0.45); }

.hs-bookfoot__why { color: #8a2f1c; font-size: max(13px, var(--hm-type-floor)); font-family: ${FDISP}; }

/* ---- 1366x768: fourteen to the index page, drawn smaller. */
.hs-book--compact .hs-book { padding: 9px 12px 10px; }
.hs-book--compact .hs-book__spread { padding: 9px 26px; }
.hs-book--compact .hs-book__page { padding: 11px 14px 9px; gap: 7px; }
.hs-book--compact .hs-book__head { padding-bottom: 5px; }
.hs-book--compact .hs-book__list { gap: 6px; }
.hs-book--compact .hs-band { gap: 4px; }
.hs-book--compact .hs-band__cells { gap: 5px 7px; }
.hs-book--compact .hs-spell { gap: 7px; padding: 3px 6px 3px 3px; }
.hs-book--compact .hs-spell__socket { padding: 2px; }
.hs-book--compact .hs-spell__cost { width: 26px; height: 26px; font-size: max(12px, var(--hm-type-floor)); }
.hs-book--compact .hs-spell__cost--big { width: 38px; height: 38px; font-size: max(17px, var(--hm-type-floor)); }
.hs-book--compact .hs-read { gap: 8px; }
.hs-book--compact .hs-read__plate { padding: 8px; }
.hs-book--compact .hs-rung { padding: 4px 3px; }
.hs-book--compact .hs-rung__cost { font-size: max(15px, var(--hm-type-floor)); }
.hs-book--compact .hs-rung__name { font-size: max(8px, var(--hm-type-floor)); letter-spacing: 0.1em; }
.hs-book--compact .hs-book__foot { padding-top: 6px; }

/* ============================================================= hero sheet */

/* Two halves. The left is the hero himself - face, speciality, the four
   primaries, experience, reserves - and the right is what he carries and
   commands. The right half is its own grid: the skills and the spell library
   stack in the wide column, the equipment doll takes the narrow one full
   height, and the army spans the foot of the sheet. */
.hs-hero {
  grid-template-columns: 352px minmax(0, 1fr);
  gap: 12px;
  padding: 12px;
  align-items: stretch;
}
.hs-hero__main {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 432px 436px;
  grid-template-rows: minmax(0, 1fr) auto;
  gap: 12px;
  min-width: 0;
  min-height: 0;
}
.hs-hero__skillcard { grid-column: 1; grid-row: 1; }
.hs-hero__shelf { grid-column: 2; grid-row: 1; }
.hs-hero__gear { grid-column: 3; grid-row: 1 / span 2; }
.hs-hero__armycard { grid-column: 1 / 3; grid-row: 2; min-width: 0; }
.hs-hero__skillcard, .hs-hero__gear, .hs-hero__packcard, .hs-hero__shelf,
.hs-hero__dollcard, .hs-hero__reserves {
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.hs-hero__gear { gap: 12px; }
/* The doll plate takes the slack in its column. Its sockets keep their own
   square size - stretching them turns every one into a tall rectangle and the
   artifact art spills out of it - so the slack lands on the slab they sit on,
   which is a surface and not a hole. */
.hs-hero__packcard { flex: 0 0 auto; }
.hs-hero__dollcard { flex: 1 1 auto; }
.hs-hero__reserves { flex: 1 1 auto; justify-content: space-between; }

/* Panels get the board rather than the corduroy stripe, and a gilt bead that
   reads as a moulding instead of a border. */
.hs-hero .hs-wood, .hs-hero__main .hs-wood {
  background-color: #35210f;
  background-image: ${BOARD};
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.85),
    inset 0 0 0 2px ${G1},
    inset 0 0 0 3px rgba(0,0,0,0.75),
    inset 0 3px 16px rgba(0,0,0,0.5),
    inset 0 -18px 30px rgba(0,0,0,0.28),
    0 3px 12px rgba(0,0,0,0.6);
}
.hs-card { padding: 10px 12px 11px; }
/* The rule under a card heading RUNS TO THE EDGE AND STOPS THERE. Its old ramp
   reached zero alpha at 100%, so on Settings and the Journal it faded out
   around two thirds across and the heading it was underlining had no bottom -
   a rule that does not arrive at a corner does not close anything. Two bands:
   the gilt line itself, held near full value to the last pixel, and the dark
   score under it that makes it read as cut rather than drawn. */
.hs-card__title {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: max(11px, var(--hm-type-floor));
  letter-spacing: 0.16em;
  margin-bottom: 8px;
  padding-bottom: 5px;
  color: #e8c979;
  background-image:
    linear-gradient(90deg,
      rgba(168,128,31,0.90) 0%, rgba(168,128,31,0.62) 62%, rgba(168,128,31,0.52) 100%),
    linear-gradient(90deg,
      rgba(20,13,4,0.70) 0%, rgba(20,13,4,0.44) 62%, rgba(20,13,4,0.38) 100%);
  background-size: 100% 1px, 100% 1px;
  background-position: 0 calc(100% - 1px), 0 100%;
  background-repeat: no-repeat, no-repeat;
}
/* The heading's terminus. Every heading in this kit was a word and a rule, and
   a rule with nothing at either end is a border; the classic sets a gem at the
   head of a carved band and lets the band run out from it. The kit's gem, drawn
   in CSS rather than from the token because at 6px the token's black setting
   would be half the figure. */
.hs-card__title::before {
  content: "";
  flex: 0 0 auto;
  width: 6px;
  height: 6px;
  transform: rotate(45deg);
  background-image: linear-gradient(135deg, #f6e3ac, ${G2} 55%, #4b3410);
  box-shadow: 0 0 0 1px rgba(0,0,0,0.75);
}
/* A dotted leader is a printed-index idiom. On a carved panel the leader is a
   scored line, and it stops well short of both ends. */
.hs-hero .hm-statline__leader {
  background-image: linear-gradient(90deg,
    rgba(0,0,0,0.55) 0 60%, rgba(255,226,176,0.10) 60% 100%);
  background-size: 100% 2px;
  background-position: 0 60%;
  background-repeat: repeat-x;
  opacity: 0.5;
}
.hs-hero .hm-statline { padding: 3px 0; }
.hs-hero .hm-statline__value { font-family: ${FDISP}; font-size: max(14px, var(--hm-type-floor)); color: #f4e6c2;
  text-shadow: 0 1px 0 rgba(0,0,0,0.9); }
.hs-hero .hm-statline__label { font-size: max(11px, var(--hm-type-floor)); letter-spacing: 0.08em; }

/* ---- portrait */
/* The frame itself is in \`styles.ts\` now, on the base \`.hs-portrait\`, so the
   town screen's six portraits and every dialog's get the carved gilt frame
   rather than only the hero sheet. What was here - a second flat gilt ring, one
   pixel wider than the one it overrode - is gone; there is nothing left for
   this file to say about a portrait. The block-and-centre rules moved with it,
   because they were the half that was actually load-bearing: the card is a
   block, so \`align-self: center\` never applied and the frame ran the full width
   with the face pushed into one corner of it. */
.hs-hero__name { font-size: max(22px, var(--hm-type-floor)); letter-spacing: 0.05em; margin-top: 7px;
  color: #f6e2ac; text-shadow: 0 2px 0 #2c1f08, 0 3px 10px rgba(0,0,0,0.8); }
.hs-hero__class { font-size: max(12px, var(--hm-type-floor)); letter-spacing: 0.09em; text-transform: uppercase; }
.hs-hero__hair { margin: 9px 0 8px; }

/* ---- primaries: four struck plaques beside the face, not four wide tiles
   across the sheet. Four small numbers want a card the size of four small
   numbers. */
.hs-primaries { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 7px; }
.hs-prim {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 7px 2px 6px;
  min-width: 0;
${reliefDecls("chamfer", "bronze", 2)}
}
/* Every medallion on this sheet sits in the same cut, and the sprite screens
   onto its floor: \`ui.*\` art is opaque with a near-black ground baked in. */
.hs-prim__socket,
.hs-shelf__icon {
  flex: 0 0 auto;
  display: flex;
  padding: 2px;
  isolation: isolate;
${reliefDecls("recess", "bronze", 2)}
}
.hs-prim__value {
  font-family: ${FDISP};
  font-size: 25px;
  line-height: 1.05;
  color: #fff3d2;
  text-shadow: 0 2px 0 rgba(0,0,0,0.85), 0 0 12px rgba(255,214,140,0.35);
}
.hs-prim__label {
  font-family: ${FDISP};
  font-size: max(9px, var(--hm-type-floor));
  line-height: 1.15;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  text-align: center;
  color: #e2c894;
  text-shadow: 0 1px 0 rgba(0,0,0,0.8);
}

/* ---- secondary skills: eight carved tablets, the art the size the art was
   drawn at, and the rules text in the tooltip. Printed on the tablet it made
   these eight the largest thing on a sheet that has no room to spare. */
.hs-skills {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: minmax(0, 1fr);
  gap: 10px;
  flex: 1 1 auto;
  min-height: 0;
}
.hs-skill {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 9px 8px 8px;
  min-width: 0;
  overflow: hidden;
${reliefDecls("chamfer", "stone", 2)}
}
.hs-skill__socket {
  flex: 0 0 auto;
  padding: 3px;
  isolation: isolate;
${reliefDecls("recess", "bronze", 2)}
}
.hs-skill__name {
  font-family: ${FDISP};
  font-size: max(15px, var(--hm-type-floor));
  letter-spacing: 0.02em;
  text-align: center;
  color: #fbeecb;
  text-shadow: 0 1px 0 rgba(0,0,0,0.9), 0 2px 6px rgba(0,0,0,0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.hs-skill__level {
  font-family: ${FDISP};
  font-size: max(10px, var(--hm-type-floor));
  letter-spacing: 0.19em;
  text-transform: uppercase;
  color: #ecc87f;
  text-shadow: 0 1px 0 rgba(0,0,0,0.9);
}

/* ---- speciality, inside the portrait card */
.hs-special { display: flex; align-items: center; gap: 10px; }
.hs-special__socket {
  flex: 0 0 auto;
  padding: 4px;
  isolation: isolate;
${reliefDecls("recess", "bronze", 2)}
}
.hs-special__kind { letter-spacing: 0.14em; text-transform: uppercase; font-size: max(9px, var(--hm-type-floor)); }
.hs-special__name { font-family: ${FDISP}; font-size: max(17px, var(--hm-type-floor)); color: #f4e6c2;
  text-shadow: 0 1px 0 #000; }

/* ---- the spell library */
.hs-shelf {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  gap: 4px;
  padding: 7px 9px;
  overflow: hidden;
  border: 0;
  cursor: pointer;
  min-height: 0;
${reliefDecls("recess", "wood", 2)}
}
.hs-shelf__band { display: flex; align-items: center; gap: 12px; min-height: 0; }
.hs-shelf__rank {
  font-family: ${FDISP};
  font-size: max(11px, var(--hm-type-floor));
  letter-spacing: 0.17em;
  text-transform: uppercase;
  color: #d8b571;
  text-shadow: 0 1px 0 rgba(0,0,0,0.9);
  flex: 0 0 auto;
  width: 62px;
  text-align: right;
  white-space: nowrap;
}
.hs-shelf__icons { display: flex; flex-wrap: wrap; gap: 6px; min-width: 0; }
.hs-shelf__icon {
  isolation: isolate;
${reliefDecls("recess", "bronze", 1)}
}
.hs-shelf:hover { filter: brightness(1.24); }

/* ---- the doll: sockets cut into a leather-backed plate with the figure
   etched behind them, so the gaps in the arrangement read as a body and not as
   holes in a grid. */
.hs-doll {
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  align-content: space-evenly;
  gap: 6px;
  padding: 10px;
  min-height: 0;
  position: relative;
  isolation: isolate;
${reliefDecls("recess", "wood", 3)}
}
/* The etched figure rides on a layer of its own now that the plate's face is
   the primitive's: it is what is *behind* the sockets, not a second material. */
.hs-doll::before {
  content: "";
  position: absolute;
  inset: 3px;
  z-index: -1;
  pointer-events: none;
  background-image: ${FIGURE};
  background-repeat: no-repeat;
  background-position: 50% 46%;
  background-size: auto 94%;
}
.hs-doll .hs-slot { width: 100%; max-width: 72px; height: auto; aspect-ratio: 1 / 1;
  justify-self: center; }
.hs-backpack { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 6px;
  align-content: flex-start; grid-auto-rows: min-content; }
.hs-backpack .hs-slot { width: 100%; max-width: 72px; height: auto; aspect-ratio: 1 / 1;
  justify-self: center; }

/* Sockets are recessed, and the art sits inside the rim with a margin. An icon
   drawn larger than its socket crosses the rim and the plate stops reading as a
   row of settings. */
.hs-hero .hs-slot {
  isolation: isolate;
${reliefDecls("recess", "bronze", 2)}
}
.hs-hero .hs-slot--empty { opacity: 1; }
.hs-slot__ghost {
  position: absolute;
  left: 50%; top: 50%;
  transform: translate(-50%, -50%);
  font-family: ${FDISP};
  font-size: max(9px, var(--hm-type-floor));
  letter-spacing: 0.14em;
  color: rgba(232,205,152,0.26);
  text-shadow: 0 1px 0 rgba(0,0,0,0.8);
  pointer-events: none;
  white-space: nowrap;
}

/* ---- experience */
/* The figures sit on their own plaque above the bar. Printed on the fill they
   straddle the lit/unlit boundary and half of each numeral disappears. */
.hs-xp { display: flex; align-items: baseline; gap: 8px; margin-bottom: 6px; }
.hs-xp__now {
  font-family: ${FDISP};
  font-size: max(21px, var(--hm-type-floor));
  color: #fff0c9;
  text-shadow: 0 2px 0 rgba(0,0,0,0.9), 0 0 12px rgba(255,214,140,0.3);
}
.hs-xp__of { font-family: ${FDISP}; font-size: max(13px, var(--hm-type-floor)); color: #c9ab72; }
.hs-xp__note { margin-top: 5px; }
.hs-bar {
  height: 16px;
${reliefDecls("recess", "stone", 2)}
}
.hs-bar__fill {
  background-image:
    repeating-linear-gradient(115deg, rgba(255,255,255,0.12) 0 4px, rgba(0,0,0,0) 4px 9px),
    linear-gradient(180deg, #ffe6a6 0%, ${G3} 38%, #8f6a14 100%);
  box-shadow: 0 0 10px rgba(217,178,90,0.45), inset 0 1px 0 rgba(255,250,225,0.6);
}

/* ---- army: seven sockets at something near the size the creature was painted
   at. A 56px thumbnail of a 240px Angel is why this band read as a footnote. */
.hs-hero .hs-armyrow { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.hs-hero .hs-armyrow__head { display: flex; align-items: center; gap: 6px;
  font-family: ${FDISP}; font-size: max(12px, var(--hm-type-floor)); letter-spacing: 0.16em; text-transform: uppercase;
  color: ${G3}; text-shadow: 0 1px 0 #000; }
.hs-hero .hs-armyrow__slots { display: flex; gap: 8px; }
.hs-hero__muster { display: flex; align-items: stretch; gap: 18px; }
.hs-hero__muster .hs-armyrow { flex: 0 0 auto; }
.hs-hero .hs-armyrow .hs-slot { flex: 0 0 auto; width: 144px; height: 144px; }
.hs-hero .hs-slot__count {
  min-width: 44px;
  padding: 1px 6px 2px;
  font-family: ${FDISP};
  font-size: max(15px, var(--hm-type-floor));
  line-height: 18px;
  color: #fbeaba;
  background-image:
    linear-gradient(180deg, rgba(60,44,22,0.96), rgba(12,8,3,0.98));
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.9),
    inset 0 1px 0 rgba(255,226,176,0.22),
    0 0 0 1px rgba(107,77,21,0.9);
  text-shadow: 0 1px 0 rgba(0,0,0,0.9);
}
.hs-hero__hint { margin-top: 7px; }

/* ================== the kit's tokens at their other call sites =============
 *
 * The kit owns ONE parchment (\`--hm-vellum\` above) and one ornament, and the
 * brief that authored them enumerated the places carrying a byte-copy of the
 * comb they replace. Two of those live in files this sheet owns and name the
 * token directly. The rest are screens owned by other workstreams, so the token
 * reaches them from here.
 *
 * Each rule below restates that site's OWN layer stack with the comb swapped
 * for the token and nothing else touched - same radials, same base, same order
 * - and carries \`.hm-root\` for one reason: a screen's own sheet is the
 * \`screen\` layer and this is the \`polish\` layer below it (see
 * \`theme/styleSheet.ts\`), so an override at equal specificity would lose. \`.hm-root\` is the theme provider
 * and is an ancestor of every screen.
 *
 * ## EVERY ONE OF THEM RESTATES background-size AND background-repeat
 *
 * This is not tidiness, it is the bug the last round shipped. Both properties
 * are LISTS that cycle when they are shorter than \`background-image\`, and
 * \`reliefDecls\` emits three entries of each for a wood plate - the last of
 * them \`331px 331px\`, the wood fibre's tile. Swapping a one-layer comb for the
 * token's three layers moves the opaque base gradient from slot 3 to slot 5, so
 * it inherited \`331px 331px\` and \`repeat\`, and a smooth base gradient tiled at
 * 331px is a sawtooth. Measured down the Journal's chronicle card at x=903-914:
 * a 35-luma butt join at y=384, y=715 and y=1046, three flat slabs down the
 * exact centre of the screen where the round before had a maximum step of 3.1.
 * A single \`auto\`/\`repeat\` applies to every layer however many there are, which
 * is why it is stated as one value and not as a matching list.
 *
 * These belong inlined at their call sites; they are here because a comb that
 * exists in seven copies cannot be fixed in one file that owns four. */

/* Hall of Fame: the leaf, and the board it lies on. */
.hm-root .hm-hof__leaf {
  position: relative;
  background-image:
    var(--hm-vellum),
    radial-gradient(80% 60% at 88% 96%, rgba(120,86,38,0.36) 0%, rgba(0,0,0,0) 60%),
    radial-gradient(120% 140% at 50% 12%, #cdb385 0%, #ab9061 46%, #7e6840 100%);
  background-size: auto;
  background-repeat: repeat;
}
.hm-root .hs-screen.hm-hof-screen {
  background-image:
    ${BOARD_FIGURE},
    radial-gradient(90% 70% at 50% 6%, rgba(255,214,150,0.12) 0%, rgba(0,0,0,0) 60%),
    radial-gradient(120% 95% at 50% 0%, rgba(0,0,0,0) 20%, rgba(0,0,0,0.85) 100%),
    linear-gradient(180deg, #3a2312 0%, #221408 58%, #120a04 100%);
  background-size: auto;
  background-repeat: repeat;
}

/* ---- THE THREE MULTIPLIER TABLES ARE WELLS CUT INTO THE LEAF.
   Sampled over the lower half of the shipped page (y660-1050) the whole band
   ran a mean of 145 with a standard deviation of 22 and 73% of its pixels in
   the single octile 128-160: tan ground, tan rows, tan numerals, tan rules, six
   typographic registers stacked with nothing to anchor the eye. The reference
   is bimodal - a mid-value field, near-black inset slot wells, and gilt as the
   only bright value - and that is a composition rule, not a colour scheme: the
   figures a player compares live in a hole, and the page is what surrounds it.
   So each column becomes a struck well under 40 luma, its heading becomes gilt
   on that well rather than brown on paper, and the alternating tint the rows
   carried on paper goes: on a dark ground a 0.34-alpha cream band is a stripe,
   not a rule. */
.hm-root .hm-hof__ladder { gap: 0 30px; margin-top: 4px; }
.hm-root .hm-hof__lcol {
  padding: 5px 11px 6px;
  background-color: #1d1309;
  background-image:
    var(--hm-vellum),
    radial-gradient(120% 70% at 50% 0%, rgba(255,214,150,0.10) 0%, rgba(0,0,0,0) 62%),
    linear-gradient(178deg, #2c1d0c 0%, #1d1308 58%, #120b04 100%);
  background-size: auto;
  background-repeat: repeat;
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.92),
    inset 0 2px 7px rgba(0,0,0,0.72),
    inset 0 -1px 0 rgba(255,226,176,0.13),
    0 1px 0 rgba(255,250,225,0.55);
}
.hm-root .hm-hof__lhead {
  color: #e0bb6a;
  text-shadow: 0 1px 0 rgba(0,0,0,0.9);
  background-image: linear-gradient(90deg, rgba(168,128,31,0.85) 0%, rgba(168,128,31,0.55) 100%);
}
.hm-root .hm-hof__lrow,
.hm-root .hm-hof__lrow:nth-child(even),
.hm-root .hm-hof__lrow:nth-child(odd) {
  background-color: transparent;
  background-image: linear-gradient(90deg, rgba(255,226,176,0.15) 0%, rgba(255,226,176,0.06) 100%);
}
.hm-root .hm-hof__lcol .hm-hof__lrow:last-child { background-image: none; }
/* The wells cost the page thirteen pixels of height it did not have - the
   worked example under them is the last line on the leaf and the leaf clips -
   so the rows give back what the frame took. */
.hm-root .hm-hof__lrow { padding: 5px 4px 4px; }
.hm-root .hm-hof__llabel { color: #e6d3a4; text-shadow: 0 1px 0 rgba(0,0,0,0.9); }
/* The numeral is set in the bitmap face with a cream keyline, which on paper
   read as a ghosted double image of a serif digit. The keyline is a prop of the
   component and not ours to change; what IS ours is the ground it is struck on,
   and a cream outline round a dark body over a near-black well is a gilt
   numeral rather than an accident. */
/* ONE COLOUR FOR THE WHOLE NUMERAL.
   The figure is set in the bitmap face with a keyline named by the component -
   \`keyline="#f7edcf"\` - which on paper drew a cream halo offset off a dark
   body, so every factor read as a ghosted double image of a serif digit. The
   keyline is not ours to remove; its COLOUR relationship to the body is, and a
   body set to the keyline's own value merges the two into one solid mark. The
   ghost is gone and the digits are gilt on a black well, which is where the
   reference puts a number a player compares. */
.hm-root .hm-hof__lfactor { color: #f7edcf; }
.hm-root .hm-hof__ltimes { color: #b08d34; text-shadow: 0 1px 0 rgba(0,0,0,0.9); }

/* Journal: the desk, the oath band, the two wells and the cards.
   The whole screen measured a mean luma of 53 with 94% of its pixels under 96 -
   a book that is unreadable as a book - so every ground here is lifted a stop
   and the well's own vignette is halved. The type on it is cream at 0.9 alpha
   or better, so the lift costs no contrast. */
.hm-root .hs-screen.hm-jn {
  background-image:
    radial-gradient(120% 80% at 50% -6%, rgba(140,108,60,0.32) 0%, rgba(0,0,0,0) 62%),
    radial-gradient(90% 70% at 50% 108%, rgba(0,0,0,0.52) 0%, rgba(0,0,0,0) 60%),
    var(--hm-vellum),
    linear-gradient(174deg, #3d2a19 0%, #2a1d11 55%, #17100a 100%);
  background-size: auto;
  background-repeat: repeat;
}
.hm-root .hm-jn__oathrow {
  background-image:
    var(--hm-vellum),
    linear-gradient(178deg, rgba(96,70,38,0.50) 0%, rgba(40,28,14,0.62) 100%);
  background-size: auto;
  background-repeat: repeat;
}
.hm-root .hm-jn__card {
  background-image:
    radial-gradient(88% 55% at 50% 0%, rgba(255,224,176,0.10) 0%, rgba(0,0,0,0) 62%),
    var(--hm-vellum),
    linear-gradient(178deg, #6a4e2d 0%, #553d22 48%, #3b2915 100%);
  background-size: auto;
  background-repeat: repeat;
}
/* The ruled block is inset the SAME on both sides. It was 58px on the left -
   the ledger margin's own inset - and 14px on the right, so a page whose whole
   subject is its ruling had its ruling off centre by 44px. The margin rule
   stays where a ledger puts it; the writing area is what is squared up. */
.hm-root .hm-jn__well {
  padding: 9px 58px 11px 58px;
  background-image:
    radial-gradient(120% 64% at 50% 0%, rgba(255,214,150,0.14) 0%, rgba(0,0,0,0) 56%),
    radial-gradient(134% 112% at 50% 106%, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0) 58%),
    var(--hm-vellum),
    linear-gradient(178deg, #8a6234 0%, #6f4e26 56%, #54391b 100%);
  background-size: auto;
  background-repeat: repeat;
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.9),
    inset 0 0 0 2px rgba(216,178,96,0.28),
    inset 0 2px 8px rgba(0,0,0,0.42),
    inset 0 0 60px rgba(0,0,0,0.10);
}
/* ONE LINE HEIGHT, ONE WEIGHT, A HEAD BAND AND A TAIL BAND.
   The ruling this replaces was three layers: a 26px rule, a heavier rule every
   182px, and a slow tone wave on the same 182px period. What that produced was
   rules that doubled up at 118/130, 257/270 and 438/450 with broad light bands
   between them, at a contrast of about four luma - which at 1:1 is not a ruled
   page, it is JPEG blocking. A ledger has one leading and one weight; the head
   and the tail are the only two lines on it that are different, and they are
   different because they close the block rather than because they group it. */
/* Both ruled blocks on an EMPTY page, which is the state a player first opens
   this screen in and the state the harness shoots. The page draws its ruling
   from \`.hm-jn__fill\` when there are entries and from the two pseudo-elements
   either side of the note when there are none, so a ruling authored once has to
   be named in both places or the screenshot shows the one that was not fixed. */
.hm-root .hm-jn__well--ruled .hm-jn__empty::before,
.hm-root .hm-jn__well--ruled .hm-jn__empty::after,
.hm-root .hm-jn__fill {
  background-image:
    linear-gradient(180deg,
      rgba(255,232,190,0.30) 0 1px, rgba(0,0,0,0.46) 1px 3px, rgba(0,0,0,0) 3px 4px),
    linear-gradient(0deg,
      rgba(255,232,190,0.26) 0 1px, rgba(0,0,0,0.42) 1px 3px, rgba(0,0,0,0) 3px 4px),
    repeating-linear-gradient(180deg,
      rgba(0,0,0,0) 0 25px,
      rgba(255,232,190,0.065) 25px 26px,
      rgba(0,0,0,0.085) 26px 27px);
  background-size: 100% 4px, 100% 4px, auto;
  background-position: 0 0, 0 100%, 0 5px;
  background-repeat: no-repeat, no-repeat, repeat;
}

/* Load Game: the veil over the painting, and the note plate on it. */
.hm-root .hm-sl__veil {
  background-image: var(--hm-vellum);
  background-size: auto;
  background-repeat: repeat;
}
/* THE TWO EXPLANATORY PANELS ON THIS SCREEN ARE PEERS AND NOW LOOK IT.
   One was this - an opaque slab with a flat two-stop ring and a soft drop - and
   the other was the kit's \`.hm-cart\`, which is the same job done with four
   one-sided gilt lips and a gem at each terminus. Two treatments for one thing
   on one screen is the defect; the cartouche is the one the rest of the build
   uses, so this is restated in its terms rather than the other way round. */
.hm-root .hm-sl__note {
  background-color: rgba(18,12,6,0.88);
  background-image:
    var(--hm-vellum),
    radial-gradient(90% 70% at 50% 0%, rgba(255,224,176,0.12) 0%, rgba(0,0,0,0) 66%),
    linear-gradient(178deg, rgba(96,70,38,0.55) 0%, rgba(40,28,14,0.65) 100%);
  background-size: auto;
  background-repeat: repeat;
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.92),
    inset 0 3px 0 -1px rgba(238,208,150,0.82),
    inset 3px 0 0 -1px rgba(206,168,92,0.70),
    inset -3px 0 0 -1px rgba(150,113,44,0.74),
    inset 0 -3px 0 -1px rgba(126,94,36,0.80),
    0 8px 22px rgba(0,0,0,0.62);
}
.hm-root .hm-sl__note { position: relative; }
.hm-root .hm-sl__note::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: var(--hm-gem), var(--hm-gem);
  background-position: 50% -2px, 50% calc(100% + 2px);
  background-size: 13px 13px;
  background-repeat: no-repeat;
}

/* Hot Seat: the field's weave and the plate that carries the sentence. */
.hm-root .hm-ho__grain {
  background-image: var(--hm-vellum);
  background-size: auto;
  background-repeat: repeat;
}
.hm-root .hm-ho__plate {
  background-image:
    radial-gradient(92% 68% at 50% 0%, rgba(255,226,176,0.13) 0%, rgba(0,0,0,0) 66%),
    var(--hm-vellum),
    linear-gradient(178deg, rgba(104,76,42,0.62) 0%, rgba(44,31,16,0.72) 100%);
  background-size: auto;
  background-repeat: repeat;
}

/* THE BACKDROP VEIL'S STEPS COME UNDER THE EYE'S CONTOUR THRESHOLD.
   \`ScreenBackdrop\` is what stands behind every panel screen, and its veil ran
   0.58 alpha to 0.26 in one cut at 5% of the height: measured on the Hot Seat
   shot, a row-mean step of 14.9 luma at y=53 straight across 1920px of sky, and
   two more at y=215 and y=906. It is the same defect the plate grade was
   rewritten for and the same fix - hard stops, because a smooth ramp over
   quantised art invents a colour per row, but NO STEP OVER 0.02 ALPHA, which on
   the brightest thing a town painting holds is under four levels. Twenty-five
   stops instead of five; the count is what buys the step size. */
.hm-root .hm-bd__veil {
  background-image:
    radial-gradient(92% 100% at 50% 44%,
      rgba(6,8,12,0) 0%, rgba(6,8,12,0.24) 62%, rgba(4,5,9,0.56) 100%),
    linear-gradient(180deg,
      rgba(6,8,12,0.50) 0 1.5%, rgba(6,8,12,0.48) 1.5% 3%,
      rgba(6,8,12,0.46) 3% 4.5%, rgba(6,8,12,0.44) 4.5% 6%,
      rgba(6,8,12,0.42) 6% 7.5%, rgba(6,8,12,0.40) 7.5% 9%,
      rgba(6,8,12,0.38) 9% 10.5%, rgba(6,8,12,0.36) 10.5% 12%,
      rgba(6,8,12,0.34) 12% 13.5%, rgba(6,8,12,0.32) 13.5% 15%,
      rgba(6,8,12,0.30) 15% 17%, rgba(6,8,12,0.28) 17% 19%,
      rgba(6,8,12,0.26) 19% 22%, rgba(6,8,12,0.24) 22% 27%,
      rgba(6,8,12,0.22) 27% 34%, rgba(6,8,12,0.20) 34% 44%,
      rgba(6,8,12,0.18) 44% 56%, rgba(5,6,10,0.20) 56% 64%,
      rgba(5,6,10,0.22) 64% 70%, rgba(5,6,10,0.24) 70% 76%,
      rgba(5,6,10,0.26) 76% 81%, rgba(4,5,9,0.28) 81% 86%,
      rgba(4,5,9,0.30) 86% 90%, rgba(4,5,9,0.32) 90% 94%,
      rgba(4,5,9,0.34) 94% 97%, rgba(4,5,9,0.36) 97% 100%);
  background-size: auto;
  background-repeat: repeat;
}

/* Campaign: the three panels, and the preview.
   The panels carried the same 63deg comb the rest of the build did, AND they
   overrode a three-layer image onto a wood plate without restating the sizes,
   so the lit radial at the head of each was being tiled at 1024x512 down a
   1000px column. Both are the one fix. */
.hm-root .hm-cm__rail,
.hm-root .hm-cm__main,
.hm-root .hm-cm__side {
  background-image:
    radial-gradient(90% 52% at 50% 0%, rgba(255,224,176,0.10) 0%, rgba(0,0,0,0) 62%),
    var(--hm-vellum),
    linear-gradient(178deg, #6a4e2d 0%, #553d22 48%, #3b2915 100%);
  background-size: auto;
  background-repeat: repeat;
}
/* THE MAP PREVIEW IS A PAINTING AND NOTHING PERIODIC GOES OVER A PAINTING.
   A 1px light line every 100/cells per cent in both axes is a strict grid over
   pixel art: column autocorrelation on the shipped thumbnail peaked 0.808 at
   lag 11 with harmonics at 21 and 42, which is graph paper on the largest
   object on the screen and the same failure class as the wood comb and the
   vellum comb. The caption under it already says how many cells the map is, in
   words, which is the honest place for that fact. The vignette stays: it is
   what seats the picture in its frame. */
.hm-root .hm-cm__grid { background-image: none; }
/* The two thumbnails are presented as a pair under one caption row, so they
   share an edge. Centring a square in a tall cell shares neither: measured, the
   left frame ran y437-944 against the right's y348-1050. Top-aligned, the pair
   starts on one line and the slack falls where the caption already is. */
.hm-root .hm-cm__boardbox { align-items: flex-start; }
/* Four rules, each two or three lines long, were stretched to an equal 150px so
   every one carried sixty to ninety pixels of empty bottom. A plaque is the
   size of what is cut into it. */
.hm-root .hm-cm__lore { flex: 1 1 auto; justify-content: space-between; }
.hm-root .hm-cm__lorep { flex: 0 0 auto; }

/* Settings: ONE SUBSTRATE FOR PANEL INTERIORS.
   The screen was reading as four surfaces in three materials - brown plank
   cards on the left, grey stone keybinding rows and grey stone option pills on
   the right, and a cream pill for the selected one - with nothing to say why.
   State is carried by value and gilt in this kit, not by swapping the material,
   so the rows and the pills are cut from the same oak the cards are. */
.hm-root .hm-set .hs-listrow,
.hm-root .hm-set .hs-choice {
${reliefDecls("chamfer", "wood", 1)}
}
/* Restated because the block above emits a full background stack, and the
   chosen pill has to stay the brightest thing in its row. */
.hm-root .hm-set .hs-choice--on {
  color: #221806;
  background-color: #d9b25a;
  background-image: linear-gradient(180deg, #f4e0a3, ${G3} 55%, ${G2});
  background-size: auto;
  background-repeat: no-repeat;
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.8), 0 0 12px rgba(217,178,90,0.35);
}
/* THE SAME BOARD IS NOT CUT FROM THE SAME PLACE IN THE TREE TWICE.
   \`.hs-wood\` is one figure tiled at 1024x512 with a second at 331x331, and two
   panels of equal width at the same offset draw the same knot in the same
   corner: patch correlation between PRESENTATION y240-340 and SAVING y790-890
   measured 0.678, against 0.117 between panels of different widths. The origin
   is moved per card by a pair of offsets that share no factor with either tile,
   and the face gradient - the third layer, \`no-repeat\` - is held at 0 0 so the
   lighting does not move with the figure. */
.hm-root .hm-set .hs-card.hs-wood:nth-of-type(2) {
  background-position: -397px -211px, -137px -89px, 0 0;
}
.hm-root .hm-set .hs-card.hs-wood:nth-of-type(3) {
  background-position: -733px -359px, -251px -167px, 0 0;
}
.hm-root .hm-set .hs-card.hs-wood:nth-of-type(4) {
  background-position: -181px -437px, -59px -239px, 0 0;
}

/* The corner blocks, on the two pages big enough to carry them. Same four
   tokens the plate frame uses in \`formArt.tsx\`, so a page and a painting close
   their corners the same way. The Journal well spends its own ::before on the
   ledger's red margin rule, so there the corners ride on the ::after it left. */
.hm-root .hm-hof__leaf::before,
.hm-root .hm-jn__well::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 4;
  pointer-events: none;
  background-image:
    var(--hm-corner-tl), var(--hm-corner-tr),
    var(--hm-corner-br), var(--hm-corner-bl);
  background-position: 0 0, 100% 0, 100% 100%, 0 100%;
  background-size: 30px 30px;
  background-repeat: no-repeat;
}

/* Orphan control on the one paragraph that reintroduced the defect the
   cartouche rule exists to kill: the aim's note wrapped "...seven days to take
   / one." Balanced rather than a narrower measure, for the reason written over
   \`.hm-cart__note\` - the column is whatever the page has left, so a \`ch\` cap
   cannot know where the ragged edge falls. */
.hm-root .hm-jn__aimnote { text-wrap: balance; }
`;

// --------------------------------------------------------------- mounting

/**
 * Mounts the polish sheet once, however many of these screens are open, and
 * always *after* `SCREENS_CSS`: this is the `polish` layer and that is the
 * `screens` layer, and `theme/styleSheet.ts` keeps them in that order whatever
 * mounted first. That order is what lets a rule here override one there
 * without raising specificity.
 */
export function usePolishStyles(): void {
  useStyleSheet(POLISH_STYLE_ID, POLISH_CSS, StyleLayer.polish);
}
